import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, of, Subject, forkJoin } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { User } from '../../models/User';
import { UsersService, UserCreate, UserSearchResult } from '../../services/users.service';
import { StaffService } from '../../services/staff.service';
import { StaffMember, StaffPaginatedResponse } from '../../models/StaffMember';
import { AuthService } from '../../services/auth.service';
import { ProfilesService } from '../../services/profiles.service';
import { Profile } from '../../models/Profile';
import { PermissionsService } from '../../services/permissions.service';
import { UserPermissionOverrideService } from '../../services/user-permission-override.service';
import { Permission } from '../../models/Permission';

@Component({
  selector: 'app-user-registration',
  templateUrl: './user-registration.component.html',
  styleUrls: ['./user-registration.component.css']
})
export class UserRegistrationComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  isSubmitting = false;
  submitted = false;
  showConfirmModal = false;
  showStaffTable = false;
  searchTerm = '';
  selectedStaff: StaffMember | null = null;
  useSenhaPadrao = false;
  activeTab = 'access'; // 'access', 'config', 'link', 'permissions'

  // Permissions tab state
  allPermissions: Permission[] = [];
  // Map of permissionId -> 'Allow' | 'Deny' | null
  permissionOverrideMap: { [permissionId: number]: 'Allow' | 'Deny' | null } = {};
  permissionSearchTerm = '';
  loadingPermissions = false;
  savingPermissions = false;
  
  // Modo de edição
  isEditMode = false;
  userId: number | null = null;
  loadingUser = false;

  // Dados de staff da API
  staffMembers: StaffMember[] = [];
  filteredStaff: StaffMember[] = [];
  loadingStaff = false;
  // Busca com debounce para o modal
  private searchInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  
  // Colunas para app-table (alinhadas com staff.component)
  columns: { titulo: string, label: string, formatter?: (value: any) => string }[] = [
    { titulo: 'Nome', label: 'FullName' },
    { titulo: 'CPF', label: 'CPF' },
    { titulo: 'Cargo', label: 'Position' },
    { titulo: 'Departamento', label: 'Department' },
    { titulo: 'Status', label: 'Active', formatter: (value: any) => this.formatActiveStatus(value) },
    { titulo: 'Admissão', label: 'HireDate', formatter: (value: any) => this.formatDate(value) }
  ];
  
  // Paginação
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  totalCount = 0;

  // Dados dinâmicos para os selects
  perfis: { value: number, label: string }[] = [];
  loadingProfiles = false;

  status = [
    { value: true, label: 'Ativo' },
    { value: false, label: 'Inativo' }
  ];

  // tiposContrato e tiposUsuario removidos do template; mantidos apenas os campos do formulário

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private usersService: UsersService,
    private staffService: StaffService,
    private authService: AuthService,
    private profilesService: ProfilesService,
    private permissionsService: PermissionsService,
    private userPermissionOverrideService: UserPermissionOverrideService
  ) {
    this.userForm = this.fb.group({
      name: ['', 
        [Validators.required, Validators.minLength(3), Validators.maxLength(100)]
      ],
      email: ['', 
        [Validators.required, Validators.email]
      ],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
      profileId: ['', [Validators.required]],
      active: [{ value: true, disabled: true }],
      blocked: [false],
      employeeType: [{ value: 'CLT', disabled: true }, [Validators.required]],
      isClient: [{ value: false, disabled: true }],
      isSeller: [false],
      staffId: ['']
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Carregar perfis disponíveis
    this.loadProfiles();

    // Verificar se é modo de edição
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.userId = +params['id'];
        this.loadUser(this.userId);
      }
    });

    // Formulário inicia vazio para novo cadastro
    // Debounce de pesquisa no modal de staff
    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(term => {
        this.searchTerm = (term || '').trim();
        this.currentPage = 1;
        this.carregarStaff();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Carregar perfis disponíveis
  loadProfiles(): void {
    this.loadingProfiles = true;
    this.profilesService.getAllProfiles().subscribe({
      next: (profiles: Profile[]) => {
        this.perfis = profiles.map(profile => ({
          value: profile.id,
          label: profile.name
        }));
        this.loadingProfiles = false;
        
        // Definir o primeiro perfil como padrão apenas em modo de criação
        if (!this.isEditMode && this.perfis.length > 0 && !this.userForm.get('profileId')?.value) {
          this.userForm.patchValue({
            profileId: this.perfis[0].value
          });
        }
      },
      error: (error) => {
        console.error('Erro ao carregar perfis:', error);
        this.loadingProfiles = false;
        // Fallback para perfis padrão em caso de erro
        this.perfis = [
          { value: 1, label: 'Administrador' },
          { value: 2, label: 'Gerente' },
          { value: 3, label: 'Usuário' },
          { value: 4, label: 'Operador' }
        ];
        
        // Definir o primeiro perfil como padrão apenas em modo de criação
        if (!this.isEditMode && !this.userForm.get('profileId')?.value) {
          this.userForm.patchValue({
            profileId: this.perfis[0].value
          });
        }
      }
    });
  }

  // Carregar usuário para edição
  loadUser(userId: number): void {
    this.loadingUser = true;
    this.usersService.getById(userId).subscribe({
      next: (user: User | null) => {
        if (user) {
          // Obter profileId do usuário (pode vir como array profileIds ou número profileId)
          const userProfileIds = (user as any).profileIds;
          let profileIdToSet = user.profileId; // Valor padrão
          
          // Se vier como array, pegar o primeiro ID
          if (Array.isArray(userProfileIds) && userProfileIds.length > 0) {
            profileIdToSet = userProfileIds[0];
          } else if (user.profileId) {
          }
          
          // Preencher formulário com dados do usuário
          this.userForm.patchValue({
            name: user.name,
            email: user.email,
            profileId: profileIdToSet, // Usar o profileId determinado
            active: user.active,
            blocked: user.blocked,
            employeeType: user.employeeType,
            isClient: user.isClient,
            isSeller: (user as any).isSeller ?? false,
            staffId: user.staffId
          });

          // Desabilitar campos de senha em modo de edição
          this.userForm.get('password')?.disable();
          this.userForm.get('confirmPassword')?.disable();
          this.userForm.get('password')?.clearValidators();
          this.userForm.get('confirmPassword')?.clearValidators();
          this.userForm.get('password')?.updateValueAndValidity();
          this.userForm.get('confirmPassword')?.updateValueAndValidity();

          // Carregar staff vinculado se existir
          if (user.staffId) {
            this.staffService.getStaffById(user.staffId).subscribe({
              next: (staff) => {
                if (staff) {
                  this.selectedStaff = staff as StaffMember;
                }
                this.loadingUser = false;
              },
              error: (error) => {
                console.error('Erro ao carregar staff:', error);
                this.loadingUser = false;
              }
            });
          } else {
            this.loadingUser = false;
          }
        } else {
          alert('Usuário não encontrado');
          this.router.navigate(['/users']);
        }
      },
      error: (error) => {
        console.error('Erro ao carregar usuário:', error);
        alert('Erro ao carregar usuário');
        this.router.navigate(['/users']);
      }
    });
  }

  // Validadores customizados
  passwordMatchValidator(group: AbstractControl): { [key: string]: any } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    
    if (password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  // Controlar toggle de senha padrão
  onSenhaPadraoChange(value: boolean): void {
    this.useSenhaPadrao = value;
    
    if (value) {
      // Limpar valores dos campos de senha
      this.userForm.patchValue({
        password: '',
        confirmPassword: ''
      });
      
      // Remover validadores obrigatórios dos campos de senha
      this.userForm.get('password')?.clearValidators();
      this.userForm.get('confirmPassword')?.clearValidators();
      
      // Desabilitar campos de senha
      this.userForm.get('password')?.disable();
      this.userForm.get('confirmPassword')?.disable();
    } else {
      // Reativar validadores dos campos de senha
      this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
      this.userForm.get('confirmPassword')?.setValidators([Validators.required]);
      
      // Habilitar campos de senha
      this.userForm.get('password')?.enable();
      this.userForm.get('confirmPassword')?.enable();
    }
    
    // Atualizar validade dos campos
    this.userForm.get('password')?.updateValueAndValidity();
    this.userForm.get('confirmPassword')?.updateValueAndValidity();
  }

  // Limpar formulário
  limparFormulario(): void {
    if (this.isEditMode) {
      // Em modo de edição, recarregar os dados originais
      if (this.userId) {
        this.loadUser(this.userId);
      }
    } else {
      // Em modo de criação, limpar tudo
      this.userForm.reset({
        profileId: 1,
        active: true,
        blocked: false,
        employeeType: 'CLT',
        isClient: false,
        isSeller: false
      });
      this.submitted = false;
      this.useSenhaPadrao = false;
      this.selectedStaff = null;
      // Reativar campos de senha após reset
      this.onSenhaPadraoChange(false);
    }
  }

  // Salvar usuário
  onSubmit(): void {
    this.submitted = true;
    
    if (this.userForm.valid) {
      this.showConfirmModal = true;
    }
  }

  // Confirmar cadastro
  confirmarCadastro(): void {
    this.showConfirmModal = false;
    this.isSubmitting = true;
    
    // Usar getRawValue() para obter valores de campos desabilitados também
    const formData = this.userForm.getRawValue();
    
    if (this.isEditMode) {
      // Modo de edição - atualizar usuário existente
      this.updateUser(formData);
    } else {
      // Modo de criação - criar novo usuário
      this.createUser(formData);
    }
  }

  // Criar novo usuário
  private createUser(formData: any): void {
    
    // Obter o usuário logado para CreatedBy
    const currentUser = this.authService.currentUserValue;
    const createdBy = (currentUser as any)?.name || currentUser?.email || this.authService.getUserNameFromToken() || 'Sistema';
    
    // Se usar senha padrão, definir senha como 'Mudar@123', caso contrário usar a senha informada
    const userData: UserCreate = {
      name: formData.name,
      email: formData.email,
      password: this.useSenhaPadrao ? 'Mudar@123' : formData.password,
      profileIds: [Number(formData.profileId)], // Converter para array com um único elemento
      active: formData.active,
      blocked: formData.blocked,
      employeeType: formData.employeeType,
      isClient: formData.isClient,
      isSeller: formData.isSeller ?? false,
      staffId: formData.staffId || undefined,
      staffId1: formData.staffId || undefined,
      useSenhaPadrao: this.useSenhaPadrao,
      createdBy: createdBy
    };
    

    // Criar usuário
    this.usersService.create(userData).subscribe({
      next: (user: User) => {
        
        // Se usar senha padrão, enviar email de reset de senha
        if (this.useSenhaPadrao) {
          // Chamar requestPasswordReset que enviará o código por email
          this.authService.requestPasswordReset(formData.email).subscribe({
            next: (response) => {
              this.isSubmitting = false;
              alert('Usuário cadastrado com sucesso! Foi enviado um email para que o usuário possa alterar a senha.');
              this.router.navigate(['/users']);
            },
            error: (error) => {
              this.isSubmitting = false;
              console.error('❌ Erro ao enviar email de reset:', error);
              alert('Usuário cadastrado com sucesso! No entanto, houve um erro ao enviar o email de reset de senha. Por favor, solicite o reset manualmente.');
              this.router.navigate(['/users']);
            }
          });
        } else {
          // Senha padrão desativada - usuário criado com a senha informada
          this.isSubmitting = false;
          alert('Usuário cadastrado com sucesso!');
          this.router.navigate(['/users']);
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('❌ Erro ao criar usuário:', error);
        // O ErrorService já tratou o erro visual
      }
    });
  }

  // Atualizar usuário existente
  private updateUser(formData: any): void {
    if (!this.userId) {
      console.error('ID do usuário não encontrado');
      this.isSubmitting = false;
      return;
    }

    
    // Obter o usuário logado para UpdatedBy
    const currentUser = this.authService.currentUserValue;
    const updatedBy = (currentUser as any)?.name || currentUser?.email || this.authService.getUserNameFromToken() || 'Sistema';
    
    const userData: any = {
      name: formData.name,
      email: formData.email,
      profileIds: [Number(formData.profileId)], // Converter para array com um único elemento
      active: formData.active,
      blocked: formData.blocked,
      employeeType: formData.employeeType,
      isClient: formData.isClient,
      isSeller: formData.isSeller ?? false,
      staffId: formData.staffId || undefined,
      updatedBy: updatedBy
    };
    

    this.usersService.update(this.userId, userData).subscribe({
      next: (user: User) => {
        this.isSubmitting = false;
        alert('Usuário atualizado com sucesso!');
        this.router.navigate(['/users']);
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('❌ Erro ao atualizar usuário:', error);
        // O ErrorService já tratou o erro visual
      }
    });
  }

  // Cancelar cadastro
  cancelarCadastro(): void {
    this.showConfirmModal = false;
  }

  // Getters para facilitar o acesso aos campos no template
  get f() {
    return this.userForm.controls;
  }

  // Métodos para busca de staff
  abrirBuscaUsuario(): void {
    this.showStaffTable = true;
    this.searchTerm = '';
    this.currentPage = 1;
    this.carregarStaff();
  }

  fecharBuscaUsuario(): void {
    this.showStaffTable = false;
    this.searchTerm = '';
    this.filteredStaff = [];
  }

  buscarStaff(): void {
    this.currentPage = 1;
    this.carregarStaff();
  }

  // Alinhar chamada de busca com staff.component
  onSearchChange(searchTerm: string): void {
    // Emite valor para o fluxo com debounce
    this.searchInput$.next(searchTerm || '');
  }

  // Handlers para paginação do app-table
  onPageChange(page: number): void {
    this.currentPage = page;
    this.carregarStaff();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.carregarStaff();
  }

  // Método para carregar staff da API com paginação
  carregarStaff(): void {
    this.loadingStaff = true;
    // Sempre usar paginação do backend
    const params: any = {
      page: this.currentPage,
      pageSize: this.pageSize
    };

    // Adicionar 'search' para o StaffService mapear em 'filter' no endpoint
    if (this.searchTerm && this.searchTerm.trim()) {
      params.search = this.searchTerm.trim();
    }

    this.staffService.getStaffPaginated(params).subscribe({
      next: (response: StaffPaginatedResponse) => {
        const list = Array.isArray(response?.staff) ? response.staff : [];

        // Normaliza propriedades para garantir acesso PascalCase independente do formato da API
        const prepared = list.map((item: any) => ({
          ...item,
          Id: item.Id ?? item.id,
          FullName: item.FullName ?? item.fullName ?? '',
          CPF: item.CPF ?? item.cpf ?? '',
          Position: item.Position ?? item.position ?? '',
          Department: item.Department ?? item.department ?? '',
          HireDate: item.HireDate ?? item.hireDate ?? null,
          ContractType: item.ContractType ?? item.contractType ?? 'CLT',
          Inactive: item.Inactive ?? item.inactive ?? false,
          Blocked: item.Blocked ?? item.blocked ?? false,
          Client: item.Client ?? item.client ?? false,
          Active: !(item.Inactive ?? item.inactive ?? false)
        }));

        this.staffMembers = prepared as StaffMember[];
        this.filteredStaff = prepared as StaffMember[];
        this.totalCount = response.totalCount || 0;
        this.totalPages = response.totalPages || 0;
        this.currentPage = response.page || this.currentPage;
        this.pageSize = response.pageSize || this.pageSize;
        this.loadingStaff = false;
      },
      error: (error) => {
        console.error('Erro ao carregar funcionários:', error);
        this.loadingStaff = false;
      }
    });
  }

  // Navegar para próxima página
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.carregarStaff();
    }
  }

  // Navegar para página anterior
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.carregarStaff();
    }
  }

  selecionarStaff(staff: any): void {
    this.selectedStaff = staff as StaffMember;
    const staffId = staff.Id ?? staff.id;
    const isClient = staff.Client ?? staff.client ?? false;
    const contractType = staff.ContractType ?? staff.contractType ?? 'CLT';
    const inactive = staff.Inactive ?? staff.inactive ?? false;

    this.userForm.patchValue({
      staffId: staffId ? Number(staffId) : null,
      isClient: isClient,
      employeeType: contractType as 'CLT' | 'PJ',
      active: !inactive
    });

    this.fecharBuscaUsuario();
  }

  limparStaffSelecionado(): void {
    this.selectedStaff = null;
    this.userForm.patchValue({
      staffId: null,
      active: true,
      employeeType: 'CLT',
      isClient: false
    });
  }

  getTipoVinculoLabel(staff: StaffMember): string {
    if (staff.Client) return 'Cliente';
    return staff.ContractType === 'CLT' ? 'CLT - Funcionário' : 'PJ - Prestador de Serviço';
  }

  formatDate(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR');
  }

  // Formatar status ativo para tabela
  formatActiveStatus(active: boolean): string {
    return active ? 'Ativo' : 'Inativo';
  }

  // Métodos helper para exibir valores nos inputs
  getStatusLabel(): string {
    const active = this.userForm.getRawValue().active;
    return active ? 'Ativo' : 'Inativo';
  }

  // Obter label do perfil
  getPerfil(profileId: number): string {
    const perfil = this.perfis.find(p => p.value === profileId);
    return perfil ? perfil.label : 'Não definido';
  }

  // Permissions tab methods

  loadPermissionsTab(forceReloadOverrides = false): void {
    if (!this.userId) return;
    this.loadingPermissions = true;

    const permissions$ = this.allPermissions.length > 0 && !forceReloadOverrides
      ? of({ data: this.allPermissions.map(p => ({ Id: p.id, Name: p.name, Description: p.description, GroupId: p.groupId, CreatedAt: p.createdAt, UpdatedAt: p.updatedAt, CreatedBy: p.createdBy, UpdatedBy: p.updatedBy })), pagination: null as any })
      : this.permissionsService.getPermissions(1, 1000).pipe(catchError(() => of({ data: [] as any[], pagination: null as any })));

    forkJoin({
      permissions: permissions$,
      overrides: this.userPermissionOverrideService.getByUserId(this.userId).pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ permissions, overrides }) => {
        this.permissionOverrideMap = {};
        overrides.forEach((o: any) => {
          const permId = o.PermissionId ?? o.permissionId;
          const type = (o.OverrideType ?? o.overrideType) as 'Allow' | 'Deny';
          if (permId) this.permissionOverrideMap[permId] = type;
        });

        if (this.allPermissions.length === 0) {
          this.allPermissions = (permissions.data || []).map((dto: any) => ({
            id: dto.Id,
            name: dto.Name,
            description: dto.Description,
            groupId: dto.GroupId ?? null,
            createdAt: new Date(dto.CreatedAt),
            updatedAt: new Date(dto.UpdatedAt),
            createdBy: dto.CreatedBy,
            updatedBy: dto.UpdatedBy
          }));
        }

        this.loadingPermissions = false;
      },
      error: () => {
        this.loadingPermissions = false;
      }
    });
  }

  getFilteredPermissions(): Permission[] {
    const term = this.permissionSearchTerm.toLowerCase().trim();
    if (!term) return this.allPermissions;
    return this.allPermissions.filter(p =>
      p.name.toLowerCase().includes(term) || (p.description || '').toLowerCase().includes(term)
    );
  }

  setPermissionOverride(permissionId: number, type: 'Allow' | 'Deny' | null): void {
    this.permissionOverrideMap[permissionId] = type;
  }

  savePermissions(): void {
    if (!this.userId) return;
    this.savingPermissions = true;

    const currentUser = this.authService.currentUserValue;
    const updatedBy = (currentUser as any)?.name || currentUser?.email || this.authService.getUserNameFromToken() || 'Sistema';

    const overrides = Object.entries(this.permissionOverrideMap)
      .filter(([, type]) => type !== null)
      .map(([permissionId, type]) => ({
        permissionId: Number(permissionId),
        overrideType: type as 'Allow' | 'Deny'
      }));

    this.userPermissionOverrideService.save({
      userId: this.userId,
      overrides,
      updatedBy
    }).subscribe({
      next: () => {
        this.savingPermissions = false;
        this.loadPermissionsTab(true);
      },
      error: () => {
        this.savingPermissions = false;
      }
    });
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    if (tabId === 'permissions') {
      this.loadPermissionsTab();
    }
  }

  // Navegar de volta
  voltar(): void {
    this.router.navigate(['/users']);
  }
}
